/**
 * SaveLimitModal — global modal mounted in App.tsx. Shown when an anonymous
 * user tries to save an 11th apartment (ANONYMOUS_SAVE_LIMIT = 10).
 *
 * Triggered by the `saveLimitReached` window event, which is dispatched from
 * every save callsite (VibeSearchPage, PinnedPicksSection, SearchResults) when
 * `canSaveMore()` returns false.
 *
 * Voice (per Design System §5 / Voice Spec): warm, frames it as momentum
 * rather than a gate. "Your list is getting good." — not "You've reached
 * your limit."
 *
 * Layout (Paper board 6HR-0): bottom sheet on mobile, centered card on md+.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { sheetOfferEnabledSnapshot, subscribeSheetOfferEnabled } from './sheetOfferFlag';

export function SaveLimitModal() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const sheetOfferEnabled = useSyncExternalStore(subscribeSheetOfferEnabled, sheetOfferEnabledSnapshot, () => null);
  // On /vibe the sheet is the only pricing offer once the flag is on.
  // Unknown stays on the sheet pitch so the price-report link does not flash.
  const sheetPitch = pathname.startsWith('/vibe') && sheetOfferEnabled !== false;

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('saveLimitReached', handler);
    return () => window.removeEventListener('saveLimitReached', handler);
  }, []);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-end md:items-center justify-center md:p-4 animate-fade-in"
      style={{ background: 'rgba(14, 11, 9, 0.85)', backdropFilter: 'blur(6px)' }}
      onClick={() => setOpen(false)}
      data-testid="save-limit-prompt"
    >
      <div
        className="w-full max-w-md rounded-t-3xl md:rounded-3xl px-6 pt-3 pb-6 md:pt-6 space-y-4"
        style={{
          backgroundImage:
            'linear-gradient(180deg, #322D24 0%, #232030 100%)',
          backgroundColor: 'var(--ts-bg-elevated)',
          border: '1px solid rgba(255,200,60,0.26)',
          borderTop: '1px solid rgba(255,210,77,0.35)',
          boxShadow: '0 0 22px rgba(255,190,40,0.12), var(--ts-shadow-lg)',
          paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom, 0px))',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile sheet affordance) */}
        <div className="flex justify-center md:hidden" aria-hidden="true">
          <div
            style={{
              width: 44,
              height: 5,
              borderRadius: 999,
              background: 'var(--ts-border-default)',
            }}
          />
        </div>

        {/* Glowing taco */}
        <div className="text-center">
          <span
            className="text-4xl inline-block"
            style={{ filter: 'drop-shadow(0 0 14px var(--ts-accent-glow))' }}
            aria-hidden="true"
          >
            🌮
          </span>
        </div>

        <h3
          className="text-center"
          style={{
            fontFamily: 'var(--ts-font-display)',
            fontWeight: 800,
            fontSize: 22,
            color: 'var(--ts-text-primary)',
          }}
        >
          Your list is getting good.
        </h3>
        <p
          className="text-center"
          style={{ color: 'var(--ts-text-secondary)', fontSize: 14, lineHeight: 1.5 }}
        >
          {sheetPitch
            ? "That's 10 saved. Alexander can put a personalized sheet together for this search, with current pricing and availability."
            : "That's 10 saved, a great size for a price report. Request yours and we'll pull real pricing and availability on all of them (your account comes free with it, so your list keeps growing)."}
        </p>
        <div className="flex flex-col gap-2">
          {sheetPitch ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                window.dispatchEvent(new Event('taco:focus-sheet-offer'));
              }}
              className="w-full py-3 rounded-full font-bold text-center transition-all hover:-translate-y-0.5"
              style={{
                background: 'var(--ts-accent-primary)',
                color: 'var(--ts-accent-contrast, #0b0b0c)',
                boxShadow: '0 0 24px -6px var(--ts-accent-glow)',
              }}
              data-testid="save-limit-sheet-cta"
            >
              Get my sheet from Alexander
            </button>
          ) : (
            <Link
              to="/price-report"
              onClick={() => setOpen(false)}
              className="w-full py-3 rounded-full font-bold text-center transition-all hover:-translate-y-0.5"
              style={{
                background: 'var(--ts-accent-primary)',
                color: 'var(--ts-accent-contrast, #0b0b0c)',
                boxShadow: '0 0 24px -6px var(--ts-accent-glow)',
              }}
              data-testid="save-limit-report-cta"
            >
              Get my price report →
            </Link>
          )}
          <button
            onClick={() => setOpen(false)}
            className="w-full py-2 text-sm underline underline-offset-4"
            style={{ color: 'var(--ts-text-secondary)' }}
            data-testid="save-limit-dismiss"
          >
            Continue browsing
          </button>
        </div>
      </div>
    </div>
  );
}

export default SaveLimitModal;
